import { useEffect, useRef } from 'react';
import { logAnalyticsEvent } from '../utils/analytics';

/**
 * Engagement tracking for content pages (STACK-479).
 *
 * Fires `page_read` once per content view when the reader has both scrolled
 * past half the content AND spent enough *active* time on it, and fires
 * `page_scroll_depth` once per depth milestone reached.
 *
 * "Active" excludes time while the tab is hidden, so a page left open
 * overnight never produces a read.
 *
 * All state lives in refs keyed by `surface::contentId`, which gives two
 * properties the ticket asks for at once:
 *   - SPA navigation to different content resets the view (two reads).
 *   - A React 18 StrictMode double-mount does NOT reset it — StrictMode
 *     preserves refs across its simulated unmount, and the reset is keyed on
 *     the view rather than on the effect running, so `page_read` fires once.
 */

export const READ_SCROLL_THRESHOLD_PCT = 50;
export const READ_DWELL_MS = 15000;
export const SCROLL_MILESTONES = [25, 50, 75, 100];

// How often we re-evaluate dwell/scroll without a user event. Also what makes
// the read fire for someone who scrolled once and then sat still reading.
const TICK_MS = 1000;

const createViewState = () => ({
  maxScrollPct: 0,
  contentLengthPx: 0,
  fitViewport: false,
  accumulatedMs: 0,
  resumedAt: null,
  readFired: false,
  milestonesFired: new Set(),
});

/**
 * @param {Object}  options
 * @param {string}  options.surface      one of blog_post | supplement_detail |
 *                                       condition_detail | research_article |
 *                                       research_deep_dive
 * @param {string}  options.contentId    blog slug / supplement key / condition
 *                                       key / article id
 * @param {string}  [options.contentTitle] human-readable title, when available
 * @param {Object}  options.contentRef   ref to the element holding the content
 * @param {Object}  [options.extraProps] extra event properties; the spec'd
 *                                       properties always win over these
 * @param {boolean} [options.enabled]    false pauses tracking without
 *                                       discarding the current view's progress
 */
export default function usePageReadTracking({
  surface,
  contentId,
  contentTitle,
  contentRef,
  extraProps,
  enabled = true,
}) {
  const viewRef = useRef(null);
  const viewKeyRef = useRef(null);
  const latestRef = useRef({});

  // Keep fire-time metadata fresh without writing refs during render.
  useEffect(() => {
    latestRef.current = { surface, contentId, contentTitle, extraProps };
  });

  const viewKey = `${surface}::${contentId == null ? '' : contentId}`;

  useEffect(() => {
    if (!enabled || !surface || contentId == null || contentId === '') return undefined;
    if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;

    // Reset only when the view actually changed — see the StrictMode note above.
    if (viewKeyRef.current !== viewKey || !viewRef.current) {
      viewKeyRef.current = viewKey;
      viewRef.current = createViewState();
    }

    let active = true;

    // Returns whether the content was measurable — i.e. whether it is on the
    // page at all. That answer also gates the dwell clock below.
    const measure = () => {
      const el = contentRef && contentRef.current;
      if (!el) return false;
      const rect = el.getBoundingClientRect();
      const height = Math.round(rect.height || el.offsetHeight || 0);
      if (height <= 0) return false;

      const view = viewRef.current;
      view.contentLengthPx = height;

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      // Content that fits entirely in the viewport counts as fully scrolled —
      // there is nothing left for the reader to scroll to. That is deliberate,
      // but it means such a view fires all four milestones on mount without
      // anyone scrolling, so the milestone event carries the fact: without it
      // a mount burst is indistinguishable from a real scroll and the 25/50/75
      // buckets cannot be cleaned up after collection.
      const fitViewport = height <= viewportHeight;
      view.fitViewport = fitViewport;
      const pct = fitViewport
        ? 100
        : Math.max(0, Math.min(100, Math.round(((viewportHeight - rect.top) / height) * 100)));

      if (pct > view.maxScrollPct) view.maxScrollPct = pct;
      return true;
    };

    const fireMilestones = () => {
      const view = viewRef.current;
      SCROLL_MILESTONES.forEach((milestone) => {
        if (view.maxScrollPct < milestone || view.milestonesFired.has(milestone)) return;
        view.milestonesFired.add(milestone);
        logAnalyticsEvent('page_scroll_depth', {
          surface: latestRef.current.surface,
          content_id: latestRef.current.contentId,
          depth_pct: milestone,
          content_length_px: view.contentLengthPx,
          fit_viewport: view.fitViewport,
        });
      });
    };

    const activeMs = () => {
      const view = viewRef.current;
      return view.accumulatedMs + (view.resumedAt == null ? 0 : Date.now() - view.resumedAt);
    };

    const resume = () => {
      const view = viewRef.current;
      if (view.resumedAt == null) view.resumedAt = Date.now();
    };

    const pause = () => {
      const view = viewRef.current;
      if (view.resumedAt == null) return;
      view.accumulatedMs += Date.now() - view.resumedAt;
      view.resumedAt = null;
    };

    const maybeFireRead = () => {
      const view = viewRef.current;
      if (view.readFired || view.maxScrollPct < READ_SCROLL_THRESHOLD_PCT) return;
      const dwellMs = activeMs();
      if (dwellMs < READ_DWELL_MS) return;

      view.readFired = true;
      const latest = latestRef.current;
      logAnalyticsEvent('page_read', {
        ...(latest.extraProps || {}),
        surface: latest.surface,
        content_id: latest.contentId,
        ...(latest.contentTitle ? { content_title: latest.contentTitle } : {}),
        dwell_seconds: Math.round(dwellMs / 1000),
        max_scroll_pct: view.maxScrollPct,
        content_length_px: view.contentLengthPx,
      });
    };

    const evaluate = () => {
      if (!active) return;
      const measurable = measure();

      // Dwell accrues only while the content is actually on the page and the
      // tab is in front. `enabled` is a coarse switch a call site sets from
      // route state; this is the check that cannot be got wrong from outside.
      // Without it, a call site whose content element unmounts (an in-page tab
      // that does not change the route) would keep banking time against this
      // content view and could fire a read for a page nobody is looking at.
      if (measurable && document.visibilityState !== 'hidden') {
        resume();
      } else {
        pause();
      }

      fireMilestones();
      maybeFireRead();

      // Everything this view can emit has been emitted. Stop measuring: the
      // 1s tick and the scroll handler each call getBoundingClientRect, which
      // forces layout, and on a long article that is a forced sync layout per
      // scroll event for the rest of the mount with nothing left to report.
      // The effect re-runs (and re-arms) if the view or `enabled` changes.
      if (isSpent()) teardown();
    };

    // Spent means the read has fired AND every milestone has. Both are set on
    // the view state, which survives a StrictMode remount, so a spent view
    // stays spent.
    const isSpent = () => {
      const view = viewRef.current;
      return view.readFired && view.milestonesFired.size === SCROLL_MILESTONES.length;
    };

    const teardown = () => {
      clearInterval(timer);
      window.removeEventListener('scroll', evaluate);
      window.removeEventListener('resize', evaluate);
      document.removeEventListener('visibilitychange', evaluate);
    };

    // Registered before the first evaluate, not after: evaluate may tear these
    // down on its own, and it cannot clear a timer that does not exist yet.
    // The first measurement is still synchronous within this effect.
    const timer = setInterval(evaluate, TICK_MS);
    window.addEventListener('scroll', evaluate, { passive: true });
    window.addEventListener('resize', evaluate);
    document.addEventListener('visibilitychange', evaluate);

    evaluate();

    return () => {
      active = false;
      clearInterval(timer);
      window.removeEventListener('scroll', evaluate);
      window.removeEventListener('resize', evaluate);
      document.removeEventListener('visibilitychange', evaluate);
      // Banking the elapsed time here is what lets `enabled` flip off and back
      // on (a nested route opening over this one) without losing or inflating
      // dwell, and what makes the StrictMode remount a no-op.
      pause();
    };
  }, [enabled, surface, contentId, viewKey, contentRef]);
}
